import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, downloadJournalPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneTilt, Plus, Trash, Sparkle, Heart, BookOpen, Star, ArrowRight, DownloadSimple } from "@phosphor-icons/react";

const TABS = [
  { id: "today", label: "Today" },
  { id: "memories", label: "Memories" },
  { id: "signs", label: "Spirit signs" },
  { id: "entries", label: "Past entries" },
];

export default function Grief() {
  const { user } = useAuth();
  const unlocked = (user?.programs || []).includes("grief180");
  const [tab, setTab] = useState("today");
  const [today, setToday] = useState(null);
  const [content, setContent] = useState("");
  const [entries, setEntries] = useState([]);
  const [memories, setMemories] = useState([]);
  const [memTitle, setMemTitle] = useState("");
  const [memContent, setMemContent] = useState("");
  const [memLoved, setMemLoved] = useState("");
  const [signsData, setSignsData] = useState({ catalog: [], spotted: [] });
  const [signNote, setSignNote] = useState({ sign: "", note: "" });

  useEffect(() => {
    if (!unlocked) return;
    api.get("/grief/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/grief/journal").then((r) => setEntries(r.data)).catch(() => {});
    api.get("/grief/memories").then((r) => setMemories(r.data)).catch(() => {});
    api.get("/grief/signs").then((r) => setSignsData(r.data)).catch(() => {});
  }, [unlocked]);

  if (!unlocked) {
    return (
      <div className="bg-white rounded-3xl border border-border p-10 text-center soft-shadow">
        <Heart size={48} weight="duotone" className="mx-auto text-clay mb-4" />
        <h1 className="font-serif text-3xl mb-2">Moving through Grief</h1>
        <p className="text-muted-foreground mb-6">A 6-month tender companion through loss. $39.99 lifetime.</p>
        <Link to="/app/pricing" data-testid="grief-unlock"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay text-white hover:bg-clay/90">
          Unlock the grief journal <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const saveEntry = async () => {
    if (!content.trim() || !today) return;
    try {
      await api.post("/grief/journal", { content, day_number: today.day });
      setContent(""); toast.success("Entry saved.");
      api.get("/grief/journal").then((r) => setEntries(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addMemory = async () => {
    if (!memTitle.trim() || !memContent.trim()) return;
    try {
      await api.post("/grief/memory", { title: memTitle, content: memContent, loved_one: memLoved });
      setMemTitle(""); setMemContent(""); setMemLoved("");
      toast.success("Memory saved.");
      api.get("/grief/memories").then((r) => setMemories(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteMemory = async (id) => {
    await api.delete(`/grief/memory/${id}`);
    setMemories((arr) => arr.filter((m) => m.id !== id));
  };

  const logSign = async (sign) => {
    try {
      await api.post("/grief/sign", { sign, note: signNote.sign === sign ? signNote.note : "" });
      setSignNote({ sign: "", note: "" });
      toast.success("Sign recorded.");
      api.get("/grief/signs").then((r) => setSignsData(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!today) return <div className="h-32" />;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">
          Moving through Grief · Day {today.day} of 180 · Week {today.week}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl font-medium">Tenderly, today.</h1>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`grief-tab-${t.id}`}
                  className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${
                    tab === t.id ? "border-clay text-clay" : "border-transparent text-muted-foreground hover:text-charcoal"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "today" && (
        <div className="space-y-6">
          <div className="relative bg-white rounded-3xl border border-border p-8 md:p-10 soft-shadow overflow-hidden">
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-clay/15 blur-3xl"/>
            <div className="relative">
              <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4 flex items-center gap-2">
                <Sparkle size={14} weight="duotone"/> Today's prompt
              </p>
              <p className="font-serif text-2xl md:text-3xl leading-snug mb-5" data-testid="grief-prompt">{today.prompt}</p>
              <textarea value={content} onChange={(e) => setContent(e.target.value)}
                        data-testid="grief-textarea" placeholder="Write freely. There is no wrong way to grieve."
                        className="w-full min-h-[200px] rounded-2xl border border-border bg-sand p-5 leading-relaxed focus:outline-none focus:ring-2 focus:ring-sage" />
              <div className="flex justify-end mt-4">
                <button onClick={saveEntry} disabled={!content.trim()} data-testid="grief-save"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay text-white hover:bg-clay/90 disabled:opacity-50">
                  <PaperPlaneTilt size={16}/> Save entry
                </button>
              </div>
            </div>
          </div>

          <div className="bg-cream/60 rounded-3xl border border-border p-6 italic font-serif text-lg" data-testid="grief-quote">
            "{today.quote}"
          </div>

          <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow">
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-3 flex items-center gap-2">
              <Star size={14} weight="duotone"/> Sign to watch for this week
            </p>
            <h3 className="font-serif text-2xl mb-2">{today.sign_of_week.sign}</h3>
            <p className="text-muted-foreground">{today.sign_of_week.meaning}</p>
          </div>
        </div>
      )}

      {tab === "memories" && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow space-y-3">
            <input value={memLoved} onChange={(e) => setMemLoved(e.target.value)} placeholder="Loved one's name (optional)"
                   data-testid="memory-loved"
                   className="w-full px-4 py-2.5 rounded-full border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage"/>
            <input value={memTitle} onChange={(e) => setMemTitle(e.target.value)} placeholder="Memory title"
                   data-testid="memory-title"
                   className="w-full px-4 py-2.5 rounded-full border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage"/>
            <textarea value={memContent} onChange={(e) => setMemContent(e.target.value)} placeholder="Tell the memory…"
                      data-testid="memory-content"
                      className="w-full min-h-[120px] rounded-2xl border border-border bg-sand p-4 focus:outline-none focus:ring-2 focus:ring-sage"/>
            <div className="flex justify-end">
              <button onClick={addMemory} data-testid="memory-add"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-clay text-white hover:bg-clay/90">
                <Plus size={16}/> Save memory
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {memories.length === 0 ? <p className="text-muted-foreground">Capture a memory above — small ones matter most.</p>
              : memories.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl border border-border p-5 soft-shadow">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-1">
                        {m.loved_one || "Memory"} · {new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </p>
                      <h4 className="font-serif text-xl mb-2">{m.title}</h4>
                      <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                    </div>
                    <button onClick={() => deleteMemory(m.id)} data-testid={`memory-delete-${m.id}`}
                            className="text-muted-foreground hover:text-destructive">
                      <Trash size={18}/>
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === "signs" && (
        <div className="space-y-6">
          <p className="text-muted-foreground">Traditional signs many believe are messages from those who've passed. Tap any sign you've noticed to record it.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {signsData.catalog.map((s) => (
              <div key={s.sign} data-testid={`sign-${s.sign}`} className="bg-white rounded-2xl border border-border p-5 soft-shadow">
                <h4 className="font-serif text-lg mb-1">{s.sign}</h4>
                <p className="text-sm text-muted-foreground mb-3">{s.meaning}</p>
                {signNote.sign === s.sign ? (
                  <div className="space-y-2">
                    <input value={signNote.note} onChange={(e) => setSignNote({ sign: s.sign, note: e.target.value })}
                           placeholder="Where/when did you see it? (optional)"
                           className="w-full px-3 py-2 text-sm rounded-full border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage"/>
                    <button onClick={() => logSign(s.sign)} className="px-4 py-1.5 rounded-full bg-clay text-white text-sm">Save</button>
                  </div>
                ) : (
                  <button onClick={() => setSignNote({ sign: s.sign, note: "" })} className="text-xs text-clay hover:underline">
                    I noticed this →
                  </button>
                )}
              </div>
            ))}
          </div>
          {signsData.spotted.length > 0 && (
            <div className="mt-8">
              <h3 className="font-serif text-2xl mb-3">Signs you've noticed</h3>
              <div className="space-y-2">
                {signsData.spotted.map((s) => (
                  <div key={s.id} className="bg-cream/60 rounded-2xl border border-border p-4">
                    <p className="font-medium">{s.sign}</p>
                    {s.note && <p className="text-sm text-muted-foreground">{s.note}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "entries" && (
        <div className="space-y-3">
          {entries.length > 0 && (
            <div className="flex justify-end">
              <button onClick={() => downloadJournalPdf("grief180")} data-testid="grief-export-pdf"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-white hover:bg-cream text-sm">
                <DownloadSimple size={14}/> Export PDF
              </button>
            </div>
          )}
          {entries.length === 0 ? <p className="text-muted-foreground">Your entries will appear here.</p>
            : entries.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl border border-border p-5 soft-shadow">
                <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">
                  Day {e.day_number} · {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
                <p className="whitespace-pre-wrap leading-relaxed">{e.content}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
